/**
 * MONSTER TOWN — TOUCH UX GUARDS. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * iPad is the primary platform, not a port. Everything in this file exists
 * because of something an iPad does that a desktop browser does not.
 *
 *  1. PINCH-ZOOMING THE PAGE. iOS ignores `user-scalable=no` on modern Safari;
 *     the only thing that reliably stops the whole app being zoomed into is
 *     preventing `gesturestart`/`gesturechange`/`gestureend`. Without this, two
 *     fingers on the world (which IS the game's zoom gesture) also zooms the
 *     browser and the kid ends up looking at a corner of the HUD.
 *
 *  2. DOUBLE-TAP-TO-ZOOM. Safari's 350 ms double-tap zoom fires on any two quick
 *     taps. A kid tapping a bed twice in a row is normal play; being thrown into
 *     a 2x page zoom for it is not. We swallow a second `touchend` inside 320 ms.
 *     (This is not a game gesture — the game has NO double-tap. See touch.ts.)
 *
 *  3. RUBBER-BAND SCROLL / PULL-TO-REFRESH on the document.
 *
 *  4. THE HARD RULE, RESTATED: nothing in this file measures with
 *     window.innerWidth/innerHeight. Orientation is decided from the CANVAS
 *     bounding rect, because on an older iPad the window and the canvas disagree
 *     and every layout decision made from `window` is wrong by a slot.
 *
 *  5. PIXEL RATIO. As soon as we see a real touch we tell the renderer it is in
 *     touch mode so it caps DPR — the single biggest win on the hand-me-down.
 *
 *  6. A 3-YEAR-OLD DOES NOT TAP. They press and rest their finger. See
 *     `installRestingFinger()` at the bottom of this file — that gesture used to
 *     do nothing at all in play mode.
 *
 * The portrait hint is a NUDGE, NOT A WALL: a small bubble at the top of the
 * screen with a picture in it. The town stays fully visible and fully playable
 * behind it, it never covers a control, and it leaves by itself. It used to be a
 * full-screen cream takeover — see the CSS note in ui.css.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import type { GameContext } from '../app/context'

const DOUBLE_TAP_MS = 320
/** Below this width/height ratio we consider the canvas "portrait". */
const PORTRAIT_RATIO = 0.86

export interface TouchUxApi {
  isPortrait(): boolean
  /** QA hook: force the overlay on/off regardless of the real orientation. */
  forceRotateOverlay(on: boolean | null): void
}

export function installTouchUx(ctx: GameContext, hud: HTMLElement): TouchUxApi {
  // -- 1. no page pinch-zoom ------------------------------------------------
  const swallow = (e: Event): void => e.preventDefault()
  document.addEventListener('gesturestart', swallow, { passive: false })
  document.addEventListener('gesturechange', swallow, { passive: false })
  document.addEventListener('gestureend', swallow, { passive: false })

  // -- 2. no double-tap page zoom ------------------------------------------
  let lastTouchEnd = 0
  document.addEventListener(
    'touchend',
    (e) => {
      const now = performance.now()
      if (now - lastTouchEnd < DOUBLE_TAP_MS) e.preventDefault()
      lastTouchEnd = now
    },
    { passive: false },
  )

  // -- 3. no rubber-band / pull-to-refresh ---------------------------------
  //
  // THE EXEMPTION LIST IS LOAD-BEARING. This handler cancels every touchmove
  // that is not inside a known scroller, and cancelling a touchmove kills the
  // NATIVE pan of whatever is under the finger. It used to name only
  // `.mt-panel-body`, which meant the build drawer's piece strips — marked
  // `overflow-x: auto; touch-action: pan-x` and therefore meant to be swiped —
  // could not be scrolled at all on a real iPad: the first move event was
  // swallowed and scrollLeft never left 0. Any element that is supposed to
  // scroll under a finger MUST be listed here.
  //
  // (The drawer no longer depends on scrolling to be usable — it wraps and
  // pages — but a strip that scrolls when swiped is still the behaviour a kid
  // expects, so the exemption stays.)
  const SCROLLERS = '.mt-panel-body, .mt-scroll, .mt-build-scroll, [data-scroll="true"]'
  document.addEventListener(
    'touchmove',
    (e) => {
      const target = e.target
      if (target instanceof Element && target.closest(SCROLLERS) !== null) return
      if (e.cancelable) e.preventDefault()
    },
    { passive: false },
  )
  document.addEventListener('dragstart', swallow)
  // iOS fires this when a long-press would select text. Long-press IS a game
  // gesture (pick a piece up), so selection must never start.
  document.addEventListener('selectstart', swallow)

  // -- 5. pixel ratio on first real touch ----------------------------------
  const onFirstTouch = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return
    ctx.renderer.setTouchMode(true)
    window.removeEventListener('pointerdown', onFirstTouch, true)
  }
  window.addEventListener('pointerdown', onFirstTouch, { capture: true, passive: true })

  // -- 4. portrait IS the layout --------------------------------------------
  //
  // ================== THE ROTATE-YOUR-IPAD HINT IS GONE ==================
  // What shipped here was a white pill floating at the top-centre of the screen
  // holding three unexplained things: a blue-to-green gradient rectangle (a
  // tablet), a rotate glyph and an orange tick. It was visible in PLAY mode
  // while the kid was simply walking around, it sat between the coin counter
  // and the camera cluster with no heading, and it read — correctly — as a
  // build widget leaking into play. In landscape it was absent entirely, so the
  // HUD composition changed between orientations with nothing explaining why.
  //
  // The root cause is not the pill's design. It is that the game was ASKING A
  // KID TO ROTATE THE DEVICE, and the brief says the opposite: "iPad is the
  // PRIMARY platform, not a port", and portrait is how a kid actually holds it.
  // A hint telling them their grip is wrong is a fail state in a game with no
  // fail states (LAW #2), and a picture a pre-reader cannot decode is LAW #4.
  //
  // So there is no hint, no overlay and no pill. Portrait REFLOWS instead — the
  // left rail goes to two columns and the piece drawer shrinks to its contents
  // (see `ui.css` and `build.css`, both keyed off `#hud[data-portrait]`, which
  // is still published from here because it is measured from the CANVAS rect
  // and nothing else in the app is allowed to measure orientation).
  // =======================================================================
  let forced: boolean | null = null

  const measure = (): boolean => {
    // CANVAS RECT. NEVER window.innerWidth/innerHeight. See the header.
    const r = ctx.canvas.getBoundingClientRect()
    if (r.width <= 0 || r.height <= 0) return false
    return r.width / r.height < PORTRAIT_RATIO
  }

  const sync = (): void => {
    hud.dataset['portrait'] = String(forced ?? measure())
  }

  sync()
  window.addEventListener('resize', () => window.setTimeout(sync, 60))
  window.addEventListener('orientationchange', () => window.setTimeout(sync, 200))
  bus.on('app:modeChanged', sync)
  // A BACKSTOP, NOT A POLL. `resize` and `orientationchange` do the real work;
  // this only catches a canvas that changed shape without either (a split-view
  // drag on iPadOS does exactly that). Twice a second, because `measure()`
  // reads a bounding rect and a rect read every sim step is 30 forced layouts a
  // second — on the 4x-throttled hand-me-down profile that is measurable, and
  // the perf gate is on CPU work per frame.
  let sinceCheck = 0
  engine.addSystem({
    id: 'ui.orientation',
    order: 93,
    fixed: (dt) => {
      sinceCheck += dt
      if (sinceCheck < 0.5) return
      sinceCheck = 0
      const want = String(forced ?? measure())
      if (hud.dataset['portrait'] !== want) hud.dataset['portrait'] = want
    },
  })

  installRestingFinger()

  return {
    isPortrait: () => forced ?? measure(),
    /**
     * Kept for the QA tools, which call `window.__ui.portrait(true|false|null)`.
     * There is no overlay left to force, so it forces the LAYOUT instead —
     * which is the more useful thing to be able to hold still for a capture.
     */
    forceRotateOverlay: (on) => {
      forced = on
      sync()
    },
  }
}

/**
 * THE 3-YEAR-OLD'S TAP. Measured, not theorised: pressing the same patch of
 * ground for 60 ms walked the monster, and pressing it for 900 ms did nothing
 * whatsoever — no walk, no interaction, no sound, no fizzle.
 *
 * WHY: `src/input/touch.ts` arms a hold at 420 ms and then swallows the tap on
 * release (`if (a.holdFired) return`). `src/app/router.ts` turns that into
 * `input:hold` — and the only subscriber in the whole game was
 * `src/build/gestures.ts`, which returns immediately unless the mode is
 * 'build'. So a slow press in PLAY mode fell on the floor.
 *
 * That is the single gesture the youngest player makes most often. A three-year
 * old does not tap, they press and rest their finger, and the game answered
 * them with silence — which reads as "broken", not as "try again". It was also
 * the closest thing in the build to an age mode: the same gesture worked for a
 * quick five-year-old and not for their little brother (LAW #3).
 *
 * THE FIX: in play mode a hold-release IS a tap. We re-emit the resolved input
 * as `input:tap` — so every existing tap subscriber (the world crew's landmark
 * hotspots included, which claim the tap for a door) sees it in exactly the
 * order the router produces — and then run the router's own play branch. The
 * sequence is byte-for-byte the tap path, so a resting finger and a quick tap
 * are literally the same event stream.
 *
 * Build mode is untouched: hold there means "pick this piece up", and the build
 * crew owns it.
 *
 * This lives in the UI/touch crew's file because `src/input` and `src/app` are
 * frozen; it is additive and adds no listener to the canvas (contract §12 —
 * only `src/app/router.ts` may do that).
 */
function installRestingFinger(): void {
  bus.on('input:hold', (e) => {
    if (e.mode === 'build') return
    bus.emit('input:tap', e)
    if (e.instanceId !== null) {
      bus.emit('play:interactRequested', { instanceId: e.instanceId, sceneId: e.sceneId })
    } else if (e.ground !== null) {
      bus.emit('play:walkTo', { sceneId: e.sceneId, target: e.ground })
    }
  })
}
